<template>
  <router-link
    v-if="type === 'link' && to"
    ref="button"
    :to="to"
    :replace="replace"
    :class="cssClasses"
  >
    <slot>Link</slot>
  </router-link>
  <a
    v-else-if="type === 'link' && href"
    ref="button"
    :href="href"
    :download="download"
    :target="download ? '_blank' : '_self'"
    :class="cssClasses"
    @click="onClick"
  >
    <div class="action-button__loader">
      <span class="action-button__loader-dot action-button__loader-dot--1"></span>
      <span class="action-button__loader-dot action-button__loader-dot--2"></span>
      <span class="action-button__loader-dot action-button__loader-dot--3"></span>
    </div>
    <div class="action-button__text">
      <slot>Link</slot>
    </div>
  </a>
  <button
    v-else
    ref="button"
    :type="type"
    :class="cssClasses"
    :disabled="disabled"
    @click="onClick"
  >
    <div class="action-button__loader">
      <span class="action-button__loader-dot action-button__loader-dot--1"></span>
      <span class="action-button__loader-dot action-button__loader-dot--2"></span>
      <span class="action-button__loader-dot action-button__loader-dot--3"></span>
    </div>
    <div class="action-button__text">
      <slot>Button</slot>
    </div>
  </button>
</template>

<script>
export default {
  name: "ActionButton",
  props: {
    circle: { type: Boolean, default: false },
    fit: { type: Boolean, default: false },
    disabled: { type: Boolean, default: false },
    download: { type: String, default: "" },
    href: { type: String, default: null },
    isLoading: { type: Boolean, default: false },
    kind: { type: String, default: "primary" },
    replace: { type: Boolean, default: false },
    small: { type: Boolean, default: false },
    to: { type: String, default: null },
    type: { type: String, default: "button" }
  },
  computed: {
    cssClasses() {
      return [
        "action-button",
        "action-button--" + this.kind,
        {
          "action-button--circle": this.circle,
          "action-button--loading": this.isLoading,
          "action-button--small": this.small,
          "action-button--fit": this.fit
        }
      ];
    }
  },
  methods: {
    onClick(event) {
      if (this.isLoading) {
        return;
      }
      this.$emit("click", event);
    },
    getDomElementRef() {
      return this.$refs.button;
    }
  }
};
</script>

<style lang="scss">
@use "src/styles/colors" as *;

@keyframes action-button-loader {
  0%,
  80%,
  100% {
    transform: scale(0);
  }
  40% {
    transform: scale(1);
  }
}

.action-button {
  font-weight: 800;
  text-decoration: none;
  background: color($gray6);
  color: color($link);
  display: inline-block;
  text-align: center;
  border: 1px solid color($gray5);
  border-radius: 4px;
  font-size: 1.1em;
  transition: all 0.2s ease;
  line-height: 1.5;
  padding: 0.7em 1em;
  position: relative;
  width: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
  appearance: none;
  margin: 0;

  &:not(:disabled) {
    -webkit-tap-highlight-color: color($gray3);
    cursor: pointer;
    user-select: none;
  }

  &:disabled {
    -webkit-tap-highlight-color: none;
  }

  .icon {
    width: 2em;
    height: 2em;
    display: block;
  }

  &__text {
    display: inline-block;
    transition: opacity 0.2s ease;

    .action-button--loading & {
      opacity: 0.3;
    }
  }

  &__loader {
    transition: opacity 0.2s ease;
    opacity: 0;
    position: absolute;
    left: 0;
    top: 0;
    bottom: 0;
    right: 0;
    display: flex;
    justify-content: center;
    align-items: center;

    .action-button--loading & {
      opacity: 1;
    }

    &-dot {
      content: "";
      display: inline-block;
      background: color($white);
      border-radius: 50%;
      width: 1em;
      height: 1em;
      margin: 0 0.5em;
      animation: action-button-loader 1.8s infinite ease-in-out;

      &--1 {
        animation-delay: -0.32s;
      }
      &--2 {
        animation-delay: -0.16s;
      }
    }
  }

  &--small {
    font-size: 0.9em;
  }

  &--primary {
    background: color($blue);
    color: color($white);

    @media (hover: hover) {
      &:hover:not(:disabled) {
        background: color($blue-highlight);
      }
    }
  }

  &--secondary {
    background: color($green);
    color: color($white);

    @media (hover: hover) {
      &:hover:not(:disabled) {
        background: color($green-highlight);
      }
    }
  }

  &--danger {
    background: color($red);
    color: color($white);

    @media (hover: hover) {
      &:hover:not(:disabled) {
        background: color($red-highlight);
      }
    }
  }

  &--plain {
    background: color($clear);
    color: color($link);
    box-shadow: none;
    border: 2px solid color($link);

    @media (hover: hover) {
      &:hover:not(:disabled) {
        background: color($gray5);
        border-color: color($gray);
        color: color($gray);
      }
    }
  }

  &--simple {
    background: color($cloud);
    border: 1px solid color($gray5);
    color: color($link);
    box-shadow: 0 0.05em 0.1em color($gray6);

    @media (hover: hover) {
      &:hover:not(:disabled) {
        background: color($gray5);
      }
    }
  }

  &--map {
    background: color($background);
    color: color($label);
    border-radius: 0.2em;
    box-shadow: 0 0 0.1em color($item-shadow);
  }

  &:disabled {
    opacity: 0.3;
    box-shadow: none;
  }
  &:active {
    box-shadow: none;
  }

  &--inline-with-input {
    margin-top: 0.3em;
  }

  &--icon {
    padding: 0.6em 0.4em 0.5em;
    box-shadow: none;
    border-radius: 0.1em;
  }

  &--fit {
    width: auto;
  }

  &--circle {
    border-radius: 100%;
    margin: 10px 10px 0 0;
    width: 3.3em;
    height: 3.3em;
    display: flex;
    justify-content: center;
    align-items: center;
    padding: 0;
  }

  &__icon {
    font-size: 1.5em;
    width: 1em;
    height: 1em;
    vertical-align: middle;
    line-height: 1em;
    margin: 0 0.2em;
  }

  .icon {
    height: 1.5em;
    width: 1.5em;
    vertical-align: middle;
    margin-right: 0.3em;
    display: inline-block;

    &--double-width {
      width: 3em;
    }

    &--small {
      height: 1em;
      width: 1em;
      vertical-align: center;
    }
  }
}
</style>
